import { useForm } from "react-hook-form";
import { basicInfoSchema, BasicInfoValues } from "../schema/basic-info.schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useBasicInfoMutation } from "../hooks/useBasicInfoMutation";
import { toast } from "react-toastify";
import { useMemo } from "react";
import {
  Form,
  FormItem,
  FormField,
  FormControl,
} from "src/components/form/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/input/Select";
import { Button } from "src/components/buttons/shadcn-button";
import { Member } from "src/redux/types/_customer.type";
import { memberKeys } from "../hooks/useFetchMembers";
import { useQueryClient } from "@tanstack/react-query";

const BasicInfoForm = ({
  member,
  subscriptionId,
  updateSelected,
}: {
  member: Member;
  subscriptionId: number;
  updateSelected: (member: Member) => void;
}) => {
  const queryClient = useQueryClient();

  const form = useForm<BasicInfoValues>({
    resolver: zodResolver(basicInfoSchema),
  });

  const { mutate: addInfoMutation, isPending } = useBasicInfoMutation();

  const handleBasicInfo = (values: BasicInfoValues) => {
    if (!member.id || !subscriptionId) {
      return toast.info("Select Member to Add Info");
    }

    addInfoMutation(
      {
        values: values,
        memberId: member.id,
        subscriptionId: subscriptionId,
      },
      {
        onSuccess: () => {
          form.reset(form.getValues());
          const updatedMember = { ...member, ...values };

          updateSelected(updatedMember);

          queryClient.setQueryData(
            memberKeys.list(subscriptionId),
            (old: Member[] | undefined) =>
              old?.map((m) => (m.id === updatedMember.id ? updatedMember : m)),
          );
        },
      },
    );
  };

  const dob = form.watch("dob");

  const age = useMemo(() => {
    if (!dob) return "--";

    const birthDate = new Date(dob);
    if (isNaN(birthDate.getTime())) return "--";

    const today = new Date();
    let years = today.getFullYear() - birthDate.getFullYear();

    const hasHadBirthdayThisYear =
      today.getMonth() > birthDate.getMonth() ||
      (today.getMonth() === birthDate.getMonth() &&
        today.getDate() >= birthDate.getDate());

    if (!hasHadBirthdayThisYear) years -= 1;

    return years >= 0 ? years : "--";
  }, [dob]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleBasicInfo)}>
        <div className="w-full max-w-md mx-auto">
          <div className="hidden sm:block overflow-x-auto">
            <table className="min-w-full border border-[#42679e] text-sm shadow-md text-[#42679e] font-medium">
              <tbody>
                {/* Blood Group */}
                <tr className="border-b border-[#42679e] even:bg-[#f9fbff] hover:bg-[#f0f6ff] transition-colors">
                  <th className="text-left px-3 py-2 font-semibold text-[#42679e]">
                    Blood Group
                  </th>
                  <td className="px-3 py-2">
                    <FormField
                      name="blood_group"
                      render={({ field }) => (
                        <FormItem>
                          <Select
                            onValueChange={field.onChange}
                            value={field.value ?? ""}
                          >
                            <FormControl>
                              <SelectTrigger className="border border-[#42679e] rounded-md focus:ring-1 focus:ring-[#42679e]">
                                <SelectValue placeholder="Select blood group" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {[
                                "A+",
                                "A-",
                                "B+",
                                "B-",
                                "O+",
                                "O-",
                                "AB+",
                                "AB-",
                              ].map((b) => (
                                <SelectItem value={b}>{b}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormItem>
                      )}
                    />
                  </td>
                </tr>

                {/* Date of Birth */}
                <tr className="border-b border-[#42679e] even:bg-[#f9fbff] hover:bg-[#f0f6ff] transition-colors">
                  <th className="text-left px-3 py-2 font-semibold text-[#42679e]">
                    Date of Birth
                  </th>
                  <td className="px-3 py-2">
                    <div className="flex items-center gap-4">
                      <FormField
                        name="dob"
                        render={({ field }) => (
                          <FormItem className="flex-1">
                            <FormControl>
                              <input
                                type="date"
                                name={field.name}
                                value={field.value ?? ""}
                                onChange={(e) => {
                                  field.onChange(e.target.value);
                                }}
                                max={new Date().toISOString().split("T")[0]}
                                className="border border-[#42679e] focus:ring-1 focus:ring-[#42679e] rounded-md px-2 py-1 w-full"
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      <span>Age: {age}</span>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div className="flex items-center justify-center my-4">
          <Button disabled={isPending}>
            {isPending ? (
              <span className="loading loading-dots loading-md"></span>
            ) : (
              "Save Basic Info"
            )}
          </Button>
        </div>
      </form>
    </Form>
  );
};

export default BasicInfoForm;
